import { formatMoney } from '../../lib/formatters.ts'
import type { Breakdown } from '../../api/cashReport'
import { sourceFocus, sourceHref } from './sourceLink.ts'

type Props = {
  open: boolean
  week: string | null
  scope?: string
  versionId: string
  breakdown: Breakdown | null
  loading: boolean
  error: string | null
  onClose: () => void
}

export default function CompanyBreakdownDrawer({
  open, week, scope, versionId, breakdown, loading, error, onClose,
}: Props) {
  if (!open) return null
  const parent = breakdown?.parent_cents
  return (
    <>
      <div className="cr-scrim" data-testid="cr-drawer-scrim" onClick={onClose} />
      <aside
        className="cr-drawer"
        data-testid="cr-drawer"
        aria-label="Company cash breakdown"
      >
        <header className="cr-drawer-head">
          <div>
            <div className="cr-eyebrow">Company breakdown</div>
            <h2>{scope === 'opening' ? 'Cash today' : `Week of ${week || '—'}`}</h2>
            <p className="cr-vid" data-testid="cr-drawer-version">version {versionId}</p>
          </div>
          <button type="button" className="cr-btn" aria-label="Close breakdown" onClick={onClose}>×</button>
        </header>
        <div className="cr-drawer-body">
          {loading ? <div className="cr-state">Loading breakdown…</div> : null}
          {error ? <div className="cr-state" data-testid="cr-drawer-error">{error}</div> : null}
          {parent != null ? (
            <p data-testid="cr-drawer-total">{formatMoney(parent / 100)}</p>
          ) : null}
          {(breakdown?.groups || []).map((g) => (
            <section key={g.project_id ?? 'company'} className="cr-drawer-section">
              <h3>{g.project_id == null ? 'Company' : `Project ${g.project_id}`}</h3>
              {g.by_source_type.map((bucket) => (
                <div key={bucket.source_type}>
                  {bucket.lines.map((line) => {
                    const href = sourceHref(line)
                    const dollars = Number(line.amount_cents) / 100
                    return (
                      <div key={sourceFocus(line)} className="cr-drawer-line">
                        <span>
                          {line.label || line.source_type}
                          {line.effective_date ? ` · ${line.effective_date}` : ''}
                          {href ? (
                            <>
                              {' '}
                              <a href={href} data-testid="cr-open-source">Open source</a>
                            </>
                          ) : null}
                        </span>
                        <b>{formatMoney(dollars)}</b>
                      </div>
                    )
                  })}
                </div>
              ))}
            </section>
          ))}
        </div>
      </aside>
    </>
  )
}
